import type { Encounter, Patient, Reference } from '@medplum/fhirtypes'
import type { TransactionMutationBuilder } from '@seen/core/bundle-builder'
import * as codeSystems from '@seen/core/codeSystems'
import { ExtendedEncounter } from '@seen/core/models/encounters/encounter'
import { NonVisitEncounter } from '@seen/core/models/encounters/nonVisitEncounter'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useMedplum } from '#src/hooks/useMedplum.ts'
import { readOptions } from '#src/query-options/readOptions.ts'
import { searchOptions } from '#src/query-options/searchOptions.ts'

interface TransferEncounterDataArgs {
  txn: TransactionMutationBuilder
  oldEncounterRef: Reference<Encounter>
  newEncounterRef: Reference<Encounter>
  /* Provide to patch diagnoses onto an existing encounter via the transaction */
  existingNewEncounter?: ExtendedEncounter
  patientRef: Reference<Patient>
  /* Provide a pre-fetched old encounter to skip re-reading it */
  prefetchedOldEncounter?: ExtendedEncounter
}

/*
 * Transfers diagnoses and medication links from one encounter to another
 * within a FHIR transaction. All writes are added to the provided
 * TransactionMutationBuilder; reads happen eagerly.
 *
 * If the new encounter already exists on the server, pass it as
 * `existingNewEncounter` so diagnoses are patched via `txn.patch()`.
 * If the new encounter is being created in the same flow, omit it and
 * set diagnoses on the new instance before calling `txn.create()`.
 */
export function useTransferEncounterData() {
  const medplum = useMedplum()
  const queryClient = useQueryClient()

  return useCallback(
    async ({
      txn,
      oldEncounterRef,
      newEncounterRef,
      existingNewEncounter,
      patientRef,
      prefetchedOldEncounter,
    }: TransferEncounterDataArgs) => {
      const oldEncounterRefString = oldEncounterRef.reference
      if (!oldEncounterRefString) {
        return { oldEncounter: undefined }
      }

      /* 1. Read old encounter (skip if pre-fetched) */
      const oldEncounter =
        prefetchedOldEncounter ??
        (await queryClient.fetchQuery(
          readOptions(medplum, ExtendedEncounter, oldEncounterRef)
        ))

      /* 2. Transfer diagnoses */
      if (oldEncounter && oldEncounter.diagnosis.length > 0) {
        if (existingNewEncounter) {
          const existingRefs = new Set(
            existingNewEncounter.diagnosis.map((d) => d.condition?.reference)
          )
          const newDiagnoses = oldEncounter.diagnosis.filter(
            (d) =>
              d.condition?.reference && !existingRefs.has(d.condition.reference)
          )
          if (newDiagnoses.length > 0) {
            txn.patch(existingNewEncounter, (enc) => {
              enc.diagnosis = [...enc.diagnosis, ...newDiagnoses]
            })
          }
        }
      }

      /* 3. Transfer active MedicationRequests (encounter field) */
      const activeMeds = await queryClient.fetchQuery(
        searchOptions(medplum, ExtendedMedicationRequest, {
          subject: patientRef,
          encounter: oldEncounterRefString,
          'status:not': ['draft', 'entered-in-error'],
          _count: 1000,
        })
      )
      for (const med of activeMeds) {
        txn.patch(med, (m) => {
          m.encounter = newEncounterRef
        })
      }

      /* 4. Transfer stopped MedicationRequests (stoppedEncounter tag) */
      const stoppedMeds = await queryClient.fetchQuery(
        searchOptions(medplum, ExtendedMedicationRequest, {
          subject: patientRef,
          status: 'stopped',
          _tag: `${codeSystems.MEDICATION_REQUEST_STOPPED_ENCOUNTER_SYSTEM}|${oldEncounterRefString}`,
          _count: 1000,
        })
      )
      for (const med of stoppedMeds) {
        txn.patch(med, (m) => {
          m.stoppedEncounter = newEncounterRef
        })
      }

      /* 5. Soft-delete old encounter if it's a non-visit encounter */
      if (
        oldEncounter &&
        NonVisitEncounter.isInstance(oldEncounter.toFhirResource())
      ) {
        txn.patch(oldEncounter, (enc) => {
          enc.status = 'entered-in-error'
        })
      }

      return { oldEncounter }
    },
    [medplum, queryClient]
  )
}
